<template>
    <div class="hotel-description">
        <LazyImage
            class="hotel-description-image"
            v-if="product.ImageUrl"
            :src="product.ImageUrl"
            :alt="product.Description" />

        <div class="hotel-description-content">
            <div>
                <div class="hotel-description-content-description">
                    {{ product.Description }}
                </div>
                <div v-if="product.Destination" class="hotel-description-content-destination">
                    {{ product.Destination }}
                </div>
                <div class="hotel-description-content-rating">
                    <Rating :rating="product.Rating" />
                </div>
            </div>

            <div class="hotel-description-content-dates">
                <div v-if="fromDate">
                    <Translation
                        class="hotel-description-content-dates-label"
                        :translate="{
                            key: 'shared.check_in',
                            suffix: ':',
                        }" />
                    {{ fromDate }}
                </div>
                <div v-if="toDate">
                    <Translation
                        class="hotel-description-content-dates-label"
                        :translate="{
                            key: 'shared.check_out',
                            suffix: ':',
                        }" />
                    {{ toDate }}
                </div>
            </div>

            <CancellationPanel :product="product" />

            <ImportantNotes
                :notes="hotelNotes"
                :title="t({ key: 'shared.important_information' })" />
        </div>
        <Divider color="lightGrey" bottomMargin="xxl" topMargin="xxl" thickness="1" />
        <Rooms :product="product" />
    </div>
</template>
<script lang="ts">
    import { computed, defineComponent, PropType } from 'vue';
    import { HotelProduct } from '@/api/Cart/cart.interfaces';
    import { t } from '@shared/helpers/translation';
    import { getLocaleWeekdayLongMonthYear } from '@shared/helpers/datetime/datetime.formatters';
    import Rating from '@ui/components/Rating/Rating.vue';
    import Translation from '@ui/components/Translation/Translation.vue';
    import LazyImage from '@ui/components/LazyImage/LazyImage.vue';
    import Rooms from '@/components/modals/Variants/Product/Variants/Hotel/Rooms/Rooms.vue';
    import Divider from '@ui/components/Divider/Divider.vue';
    import ImportantNotes from '@/components/modals/Variants/Product/Variants/Hotel/Shared/ImportantNotes/ImportantNotes.vue';
    import CancellationPanel from '@/components/checkoutPageV3/Shared/CancellationPanel/CancellationPanel.vue';

    export default defineComponent({
        name: 'HotelModalDetails',
        components: {
            ImportantNotes,
            Rooms,
            Rating,
            Translation,
            LazyImage,
            Divider,
            CancellationPanel,
        },
        methods: { t, getLocaleWeekdayLongMonthYear },
        props: {
            product: {
                type: Object as PropType<HotelProduct>,
                required: true,
            },
        },
        setup({ product }) {
            const fromDate = computed(() =>
                getLocaleWeekdayLongMonthYear(new Date(product.FromDate))
            );

            const toDate = computed(() =>
                getLocaleWeekdayLongMonthYear(new Date(product.ToDate))
            );

            const hotelNotes = computed(() =>
                Array.from(
                    new Map(
                        product.Rooms.flatMap((r) => r.AllNotes)
                            .filter((n) => n.Scope === 'Hotel')
                            .map((n) => [n.Text, n])
                    ).values()
                )
            );

            return {
                fromDate,
                toDate,
                hotelNotes,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward 'Details';
</style>
